import { NgTemplateOutlet } from '@angular/common';
import type { SimpleChanges, TemplateRef } from '@angular/core';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  Input,
  output,
  signal,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { take } from 'rxjs';

import { filterBoolean } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { LocationSelectors } from '@recruitee/locations-data';
import {
  ButtonComponent,
  IconComponent,
  RtTooltip,
  TruncateComponent,
} from '@recruitee/user-interface';

@Component({
  selector: 'rt-candidate-work-locations-button',
  imports: [
    ButtonComponent,
    NgTemplateOutlet,
    IconComponent,
    RtTooltip,
    TranslatePipe,
    TruncateComponent,
  ],
  templateUrl: './candidate-work-locations-button.component.html',
  styleUrls: ['./candidate-work-locations-button.component.less'],
  host: {
    '[class.small]': 'small',
    '[class.force-edit-icon-visible]': 'forceEditIconVisible',
    '[class.small-text]': 'fontSizeSmall',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidateWorkLocationsButtonComponent {
  private readonly translations = {
    archived: this.i18n.translate('app.locations.archived').toLowerCase(),
  };

  @Input({ required: true }) public locationIds: number[] = [];
  @Input() public canEdit: boolean;
  @Input() public emptyStateTemplate?: TemplateRef<void>;
  @Input({ transform: booleanAttribute }) public colorTextStrong = false;
  @Input({ transform: booleanAttribute }) public small = false;
  @Input({ transform: booleanAttribute }) public forceEditIconVisible = false;
  @Input({ transform: booleanAttribute }) public fontSizeSmall = false;

  public edit = output<void>();

  public workLocations = signal<string[]>([]);
  public workLocationsTooltip = computed(() => {
    if (!this.workLocations().length) {
      return '';
    }

    return `${this.i18n.translate('app.candidate.placements.work_location', {
      count: this.workLocations().length,
    })}: ${this.workLocations().join(', ')}`;
  });
  public workLocationsTooltipEnabled = computed(() => !!this.workLocations().length);

  constructor(
    private i18n: I18nService,
    private store: Store,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.locationIds?.currentValue) {
      this.setWorkLocations();
    }
  }

  private setWorkLocations(): void {
    this.store
      .select(LocationSelectors.byIds(this.locationIds))
      .pipe(filterBoolean, take(1))
      .subscribe(locations => {
        this.workLocations.set(
          locations.map(location =>
            location.archivedAt
              ? `${location.name} (${this.translations.archived})`
              : location.name,
          ),
        );
      });
  }
}
